import { currencyFormatter } from '../helper/functions';

// Color Select
class CollectionColorSwatch extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('variant-handle');
    this.sectionId = this.getAttribute('data-section');
    this.blockId = this.getAttribute('data-block') || '';
    this.quickBuyContext = this.getAttribute('data-quick-buy-context') || '';

    this.productCard = this.closest('.product-card');
    if (this.productCard) {
      this.swatch = this.querySelector('.color-swatch');
      this.swatches = this.productCard.querySelectorAll('.color-swatch');
      this.image = this.productCard.querySelectorAll('.product-card__image--primary');
      this.hoverImage = this.productCard.querySelectorAll('.product-card__image--secondary');
      this.urls = this.productCard.querySelectorAll('.url');
      this.productTitle = this.productCard.querySelector('.product-title');
      this.price = this.productCard.querySelector('sale-price');
      this.compareAtPrice = this.productCard.querySelector('compare-at-price');

      // Quickbuy variant container - desktop
      this.variantContainer = this.productCard.querySelector('.quickshop-product-variants'); // Container for the variants

      // Mobile Quickbuy button & modal
      this.mobileQuickBuyButton = this.productCard.querySelector('.product-card__quick-add-button');
      this.mobileQuickBuyModal = this.productCard.querySelector('quick-buy-modal');
    }

    this.addEventListener('click', this.onColorUpdate.bind(this));
  }

  onColorUpdate(event) {
    if (event) {
      event.preventDefault();
    }

    // Prevent multiple simultaneous requests
    if (this.isLoading) return;
    this.isLoading = true;

    this.swatches.forEach((swatch) => {
      swatch.classList.remove('is-selected');
    });
    this.swatch.classList.add('is-selected');

    fetch(`/products/${this.handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        if (!product || !product.variants) {
          throw new Error('Invalid product data');
        }
        this.updateProductDetails(product);
        this.updateVariants(product);
      })
      .catch((error) => {
        console.error('Error updating product details:', error);
        // Optionally show user-friendly error message
        this.dispatchEvent(
          new CustomEvent('swatch:error', {
            detail: { message: 'Failed to load product details' }
          })
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  updateProductDetails(product) {
    const url = product.url;
    const firstImage = product.images[0] + '&width=300';
    const secondImage = product.images.length > 1 ? product.images[1] + '&width=300' : firstImage;
    const title = product.title;
    const price = product.price / 100;
    const comparePrice = product.compare_at_price ? product.compare_at_price / 100 : null;

    this.urls.forEach((anchor) => anchor.setAttribute('href', url));
    this.image.forEach((img) => {
      img.setAttribute('src', firstImage);
      img.setAttribute('srcset', firstImage);
    });

    if (this.hoverImage && secondImage) {
      this.hoverImage.forEach((img) => {
        img.setAttribute('src', secondImage);
        img.setAttribute('srcset', secondImage);
      });
    } else if (this.hoverImage) {
      this.hoverImage.forEach((img) => {
        img.setAttribute('src', firstImage);
        img.setAttribute('srcset', firstImage);
      });
    }

    const quickBuyId = `product-quick-buy-${this.sectionId}-${this.blockId}-${this.quickBuyContext}-${product.id}`;
    if (this.mobileQuickBuyButton) {
      this.mobileQuickBuyButton.setAttribute('aria-controls', quickBuyId);
    }

    const quickBuyModal = document.querySelector(`#${quickBuyId}`);
    // check if quick buy modal already exists to prevent duplicate modals from opening
    if (!quickBuyModal && this.mobileQuickBuyModal) {
      this.mobileQuickBuyModal.setAttribute('handle', product.handle);
      this.mobileQuickBuyModal.setAttribute('id', quickBuyId);
    }

    this.productTitle.textContent = title;
    this.price.textContent = currencyFormatter.format(price);

    if (comparePrice) {
      this.compareAtPrice.querySelector('s').textContent = currencyFormatter.format(comparePrice);
      this.compareAtPrice.classList.remove('hidden');
    }
  }

  // Update variants for quickshop product
  updateVariants(product) {
    // Update mobile add button if only one variant
    if (product.variants.length == 1 && product.selling_plan_groups.length == 0) {
      const product_form = this.productCard.querySelector('form');
      product_form.querySelector('input[name="id"]').value = product.variants[0].id;
    }
    if (this.variantContainer) {
      this.variantContainer.innerHTML = '';
      let variantCount = product.variants.length;

      product.variants.forEach((variant) => {
        const variantId = variant.id;
        const variantSize = variant.option1;

        // Create a form element similar to the Liquid template
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart/add';

        // Create hidden inputs
        const onSuccessInput = document.createElement('input');
        onSuccessInput.type = 'hidden';
        onSuccessInput.name = 'on_success';
        onSuccessInput.value = 'force_open_drawer';
        form.appendChild(onSuccessInput);

        const quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = '1';
        form.appendChild(quantityInput);

        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = 'id';
        idInput.value = variantId;
        form.appendChild(idInput);

        // Create the submit button
        const button = document.createElement('button');
        button.type = 'submit';

        // Apply disabled state and styling if not available
        if (!variant.available) {
          button.disabled = true;
          button.classList.add('line-through', 'text-brand-primary-300');
        }

        //Apply width if variant count is greater than 1
        if (variantCount > 1) {
          button.classList.add('w-12');
        }

        // Format the variant size text similar to the Liquid template
        let sizeText = variantSize;
        switch (variantSize.trim()) {
          case 'X-Small':
            sizeText = 'XS';
            break;
          case 'Small':
            sizeText = 'S';
            break;
          case 'Medium':
            sizeText = 'M';
            break;
          case 'Large':
            sizeText = 'L';
            break;
          case 'X-Large':
            sizeText = 'XL';
            break;
          case 'XX-Large':
            sizeText = 'XXL';
            break;
          case 'XXX-Large':
            sizeText = 'XXXL';
            break;
          case 'Default Title':
            sizeText = 'ONE SIZE';
            break;
        }

        button.textContent = sizeText;
        form.appendChild(button);

        // Add event listener to handle form submission
        form.addEventListener('submit', (event) => {
          event.preventDefault();

          // Add to cart functionality
          const formData = new FormData(form);
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          })
            .then((response) => response.json())
            .then((data) => {
              if (data.status === 422) {
                throw new Error(data.description || 'Failed to add to cart');
              }
              // Dispatch cart:change event with the necessary details to open the drawer
              document.dispatchEvent(
                new CustomEvent('cart:change', {
                  detail: {
                    cart: { sections: {} },
                    baseEvent: 'variant:add',
                    onSuccessDo: 'force_open_drawer'
                  }
                })
              );

              // Also refresh the cart to update contents
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            })
            .catch((error) => {
              console.error('Error:', error);
              // Optionally dispatch error event for user feedback
              document.dispatchEvent(
                new CustomEvent('cart:error', {
                  detail: { message: error.message }
                })
              );
            });
        });

        this.variantContainer.appendChild(form);
      });
    }
  }
}

customElements.define('collection-swatch-selector', CollectionColorSwatch);